import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Search from "Components/DesignSystem/Search/Search";
import Table from "Components/DesignSystem/Table/Table";
import Badge from "Components/DesignSystem/Badge/Badge";
import Typography from "Components/DesignSystem/Typography/Typography";
import { TableColumn } from "types/DesignSystem/Table";
import {
  Commend3PreviewProduct,
  Commend3PreviewRecommendation,
  Commend3Strategy,
} from "../Types";
import {
  PREVIEW_EMPTY_STATE,
  PREVIEW_NO_MATCH,
  PREVIEW_SEARCH_PLACEHOLDER,
} from "../Commend3Constants";

type StrategyPreviewDrawerProps = {
  strategy: Commend3Strategy | null;
  onClose: () => void;
  onSearch: (term: string) => Promise<Commend3PreviewProduct | null>;
};

const SEARCH_DEBOUNCE_MS = 300;

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 5l10 10M15 5L5 15" />
  </svg>
);

const StrategyPreviewDrawer = ({ strategy, onClose, onSearch }: StrategyPreviewDrawerProps) => {
  const [term, setTerm] = useState<string>("");
  const [result, setResult] = useState<Commend3PreviewProduct | null>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [searched, setSearched] = useState<boolean>(false);
  const requestIdRef = useRef<number>(0);

  useEffect(() => {
    setTerm("");
    setResult(null);
    setSearched(false);
  }, [strategy?.uid]);

  useEffect(() => {
    const trimmed = term.trim();
    if (!trimmed) {
      setResult(null);
      setSearched(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const match = await onSearch(trimmed);
        if (requestIdRef.current === requestId) {
          setResult(match);
          setSearched(true);
        }
      } finally {
        if (requestIdRef.current === requestId) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [term, onSearch]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (strategy) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [strategy, onClose]);

  if (!strategy) return null;

  const columns: TableColumn<Commend3PreviewRecommendation>[] = [
    { id: "sku", header: "SKU", accessor: "sku", minWidth: "110px" },
    {
      id: "product",
      header: "Recommended Product",
      accessor: "product_name",
      minWidth: "180px",
    },
    {
      id: "mode",
      header: "Mode",
      accessor: "mode",
      minWidth: "140px",
      render: (value) => (
        <Badge variant="disabled" showIcon={false}>
          {value}
        </Badge>
      ),
    },
  ];

  return createPortal(
    <>
      <div
        className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-transition-opacity tw-z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        id="commend3-preview-drawer"
        className="tw-fixed tw-right-0 tw-top-0 tw-bottom-0 tw-w-[520px] tw-max-w-full tw-bg-white tw-shadow-lg tw-z-50 tw-flex tw-flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={`Preview — ${strategy.name}`}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-grey-200">
          <Typography variant="title-header" className="tw-text-grey-900">
            {`Preview — ${strategy.name}`}
          </Typography>
          <button
            id="commend3-preview-close"
            type="button"
            onClick={onClose}
            className="tw-p-1 tw-text-grey-600 hover:tw-text-grey-900"
            aria-label="Close preview"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-grey-200">
          <Search
            placeholder={PREVIEW_SEARCH_PLACEHOLDER}
            value={term}
            onChange={setTerm}
            variant="bordered"
          />
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4">
          {!term.trim() ? (
            <Typography variant="body-primary" className="tw-text-grey-600">
              {PREVIEW_EMPTY_STATE}
            </Typography>
          ) : searching ? (
            <Typography variant="body-primary" className="tw-text-grey-600">
              Searching…
            </Typography>
          ) : result ? (
            <>
              <div className="tw-mb-4 tw-rounded-sm tw-bg-grey-100 tw-px-3 tw-py-2">
                <Typography variant="sub-title-header" className="tw-text-grey-900">
                  {`${result.product_name} · ${result.sku}`}
                </Typography>
              </div>
              <Table<Commend3PreviewRecommendation>
                columns={columns}
                data={result.recommendations ?? []}
                rowKey={(row) => `${row.sku}-${row.mode}`}
                headerVariant="light"
                bordered
                emptyContent="No recommendations generated for this product."
              />
            </>
          ) : searched ? (
            <Typography variant="body-primary" className="tw-text-grey-600">
              {PREVIEW_NO_MATCH}
            </Typography>
          ) : null}
        </div>
      </div>
    </>,
    document.body
  );
};

export default StrategyPreviewDrawer;
